import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { visitService } from '@/services/visit.service'
import { businessService } from '@/services/business.service'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { offlineStorage } from '@/lib/offline-storage'

export function StartVisitPage() {
  const { t } = useTranslation()
  const { businessId } = useParams<{ businessId: string }>()
  const navigate = useNavigate()
  const { token } = useAuthStore()
  const isOnline = useOnlineStatus()

  const [gpsStatus, setGpsStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null)

  const { data: businessData } = useQuery({
    queryKey: ['business', businessId],
    queryFn: () => businessService.getBusiness(businessId!, token!),
    enabled: !!businessId && !!token && isOnline,
  })

  const startVisitMutation = useMutation({
    mutationFn: () =>
      visitService.create(
        {
          businessId: businessId!,
          startLatitude: location!.latitude,
          startLongitude: location!.longitude,
        },
        token!
      ),
    onSuccess: (data: any) => {
      navigate(`/scout/visits/${data.visit.id}`)
    },
  })

  useEffect(() => {
    if (gpsStatus === 'idle') {
      setGpsStatus('loading')

      if (!navigator.geolocation) {
        setGpsStatus('error')
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
          setGpsStatus('ready')
        },
        () => {
          setGpsStatus('error')
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      )
    }
  }, [gpsStatus])

  const business = (businessData as any)?.business

  const handleStartVisit = async () => {
    if (!location) return

    if (isOnline) {
      startVisitMutation.mutate()
    } else {
      // Save offline
      const offlineId = `offline-${Date.now()}-${Math.random().toString(36).slice(2)}`
      await offlineStorage.saveVisit({
        id: offlineId,
        businessId: businessId!,
        businessName: business?.name,
        startTime: new Date().toISOString(),
        startLatitude: location.latitude,
        startLongitude: location.longitude,
        isCompleted: false,
        synced: false,
      })
      navigate(`/scout/visits/${offlineId}`)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('scout.startVisit')}</h1>

          {!isOnline && (
            <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              {t('offline.visitOfflineNote')}
            </div>
          )}

          {business && (
            <div className="mb-8 p-4 bg-blue-50 rounded-lg">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{business.name}</h2>
              <p className="text-gray-600">{business.address}</p>
              <p className="text-gray-600">
                {business.city}, {business.postalCode}
              </p>
            </div>
          )}

          {/* GPS Status */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('scout.gpsStatus')}</h3>
            <div
              className={`p-4 rounded-lg ${
                gpsStatus === 'ready'
                  ? 'bg-green-50 border border-green-200'
                  : gpsStatus === 'error'
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-gray-50 border border-gray-200'
              }`}
            >
              {gpsStatus === 'loading' && (
                <div className="flex items-center text-gray-700">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-700 mr-3"></div>
                  {t('scout.gettingLocation')}
                </div>
              )}
              {gpsStatus === 'ready' && (
                <div className="text-green-700">
                  <div className="font-semibold mb-1">{t('scout.gpsReady')}</div>
                  <div className="text-sm">
                    Lat: {location?.latitude.toFixed(6)}, Lng: {location?.longitude.toFixed(6)}
                  </div>
                </div>
              )}
              {gpsStatus === 'error' && (
                <div className="text-red-700">
                  <div className="font-semibold mb-1">{t('scout.gpsError')}</div>
                  <div className="text-sm">{t('scout.gpsRequired')}</div>
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('scout.instructions')}</h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">1.</span>
                {t('scout.instruction1')}
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">2.</span>
                {t('scout.instruction2')}
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">3.</span>
                {t('scout.instruction3')}
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">4.</span>
                {t('scout.instruction4')}
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleStartVisit}
              disabled={gpsStatus !== 'ready' || startVisitMutation.isPending}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {startVisitMutation.isPending ? t('common.loading') : t('scout.startVisitNow')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
